/**
 * @fileoverview Mark-all-notifications-read mutation hook.
 * @description Wraps markAllNotificationsRead (the bell dropdown's "Mark all as read" action — one
 * POST /notifications/:id/read per unread notification, since the backend has no bulk endpoint)
 * in a TanStack mutation, invalidating the notification history on success so the read state and
 * unread count both update.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { NOTIFICATION_HISTORY_QUERY_KEY } from '@/features/notifications/hooks/useNotificationHistory'
import { NOTIFICATION_UNREAD_COUNT_QUERY_KEY } from '@/features/notifications/hooks/useUnreadNotificationCount'
import { markAllNotificationsRead } from '@/features/notifications/services/notifications.service'

/**
 * @description Marks the given notifications as read and refreshes the notification history.
 * @returns {ReturnType<typeof useMutation<void, unknown, string[]>>} The mutation object (mutate,
 * isPending, ...).
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_HISTORY_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_UNREAD_COUNT_QUERY_KEY })
    },
  })
}
